import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Flex, Text, VStack, HStack, Spinner, Divider, useDisclosure, Badge, } from "@chakra-ui/react";
import BackNavigation from "../components/BackNavigation";
import TabLayout from "../components/TabLayout";
import PassCard from "../components/event/cards/PassCard";
import { useEvent } from "../hooks/useEvent";
import { format } from "date-fns";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { getPassGroupTagBg, getPriceRange, isBookingClosed, } from "../shared-utils/utils";
import ResponsiveModal from "../components/common/ResponsiveModal";
import SlotPillsGrid from "../components/event/SlotPillsGrid";
import { FaFilter } from "react-icons/fa";
import PassesFilterDrawer from "../components/event/PassesFilterDrawer";
import EventTableBooking from "./EventTableBooking";
import EventBookModal from "../components/event/EventBookModal";
import BookTableIcon from "../icons/BookTable";
import PassIcon from "../icons/PassIcon";
import { useCustomToast } from "../components/useCustomToast";
import FastTrackEntryInfoCard from "../components/event/FastTrackEntryInfoCard";
import SelectPaxAndTime from "../components/event/SelectPaxAndTime";
const getAllPasses = (slot) => {
    if (!slot)
        return [];
    if (slot.has_pass_groups && slot.pass_groups) {
        return slot.pass_groups.flatMap((g) => g.passes);
    }
    if (slot.direct_passes)
        return slot.direct_passes;
    return [];
};
const getPassCategories = (slot) => {
    if (!slot)
        return [];
    if (slot.has_pass_groups && slot.pass_groups) {
        return slot.pass_groups.map((g) => g.name);
    }
    return ["All"];
};
const BookEvent = ({ appContext = "customer" }) => {
    var _a, _b, _c, _d, _e;
    const { eventId: event_id, bookingType } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    // Parse query params for sub_event or direct_slot
    const searchParams = new URLSearchParams(location.search);
    const subEventParam = searchParams.get("sub_event");
    const directSlotParam = searchParams.get("direct_slot");
    const tableStepParam = searchParams.get("step");
    const tabParam = searchParams.get("tab");
    const { data, isLoading, error } = useEvent(event_id);
    const [selectedTab, setSelectedTab] = useState(tabParam || (tableStepParam ? "Tables" : "Passes"));
    const [selectedSlotIdx, setSelectedSlotIdx] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [quantities, setQuantities] = useState({});
    const { isOpen, onClose, onOpen } = useDisclosure();
    const { isOpen: isEventBookModalOpen, onClose: onEventBookModalClose, onOpen: onEventBookModalOpen, } = useDisclosure();
    // const toast = useToast();
    const customToast = useCustomToast();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [hasSelectedSlot, setHasSelectedSlot] = useState(false);
    // Add state to track controlling pass quantities
    const [controllingPassQuantities, setControllingPassQuantities] = useState({});
    // Check if booking is closed
    const [bookingClosed, setBookingClosed] = useState(false);
    // Add effect to handle URL parameters for filter drawer
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const showFilters = searchParams.get("showFilters") === "true";
        setIsFilterOpen(showFilters);
    }, [location.search]);
    // Function to update URL with filter drawer state
    const updateFilterDrawerUrl = (isOpen) => {
        const searchParams = new URLSearchParams(location.search);
        if (isOpen) {
            searchParams.set("showFilters", "true");
            navigate(`?${searchParams.toString()}`);
        }
        else {
            navigate(-1);
        }
    };
    // Get event, slot, passes
    const event = data === null || data === void 0 ? void 0 : data.data;
    // Add effect to check if booking is closed
    useEffect(() => {
        setBookingClosed(isBookingClosed(event === null || event === void 0 ? void 0 : event.booking_close_time));
    }, [event === null || event === void 0 ? void 0 : event.booking_close_time]);
    // Determine sub-event and slots to show
    let slots = [];
    let eventDescription = event === null || event === void 0 ? void 0 : event.description;
    if ((event === null || event === void 0 ? void 0 : event.has_sub_events) && (event === null || event === void 0 ? void 0 : event.sub_events) && subEventParam) {
        // sub_event param format: name_id
        const subEvent = event.sub_events.find((se) => `${se.name}_${se.id}` === subEventParam);
        if (subEvent) {
            slots = subEvent.slots;
            eventDescription = subEvent.description;
        }
    }
    else if ((event === null || event === void 0 ? void 0 : event.direct_slots) && directSlotParam) {
        slots = event.direct_slots;
    }
    else if ((event === null || event === void 0 ? void 0 : event.has_sub_events) && (event === null || event === void 0 ? void 0 : event.sub_events)) {
        slots = ((_a = event.sub_events[0]) === null || _a === void 0 ? void 0 : _a.slots) || [];
        eventDescription = ((_b = event.sub_events[0]) === null || _b === void 0 ? void 0 : _b.description) || event.description;
    }
    else {
        slots = (event === null || event === void 0 ? void 0 : event.direct_slots) || [];
    }
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);
    // Find current active slot for walk-in bookings
    useEffect(() => {
        if (bookingType === "walkin" && slots.length > 0) {
            const now = new Date();
            const currentSlot = slots.find((slot) => {
                const startTime = new Date(slot.start_time);
                const endTime = new Date(slot.end_time);
                return now >= startTime && now <= endTime;
            });
            if (currentSlot) {
                setSelectedSlotIdx(currentSlot.id);
                setHasSelectedSlot(true);
            }
        }
    }, [bookingType, slots]);
    // Load saved selections from localStorage
    useEffect(() => {
        if (event_id) {
            const savedSelections = localStorage.getItem(`event_selections_${event_id}`);
            if (savedSelections) {
                const { slotIdx, quantities: savedQuantities, category, } = JSON.parse(savedSelections);
                if (slotIdx !== null) {
                    setSelectedSlotIdx(slotIdx);
                    setHasSelectedSlot(true);
                }
                if (savedQuantities) {
                    setQuantities(savedQuantities);
                }
                if (category) {
                    setSelectedCategory(category);
                }
            }
        }
        // Cleanup function to remove event selections when component unmounts
        return () => {
            if (event_id && Object.keys(quantities).length === 0) {
                localStorage.removeItem(`event_selections_${event_id}`);
            }
        };
    }, [event_id, quantities]);
    // Set initial category based on pass groups - select first group with at least one active pass
    useEffect(() => {
        if (selectedSlotIdx !== null) {
            const firstSlot = slots.find((s) => s.id === selectedSlotIdx);
            if ((firstSlot === null || firstSlot === void 0 ? void 0 : firstSlot.has_pass_groups) &&
                (firstSlot === null || firstSlot === void 0 ? void 0 : firstSlot.pass_groups) &&
                firstSlot.pass_groups.length > 0) {
                // Find the first pass group that has at least one active pass
                const groupWithActivePass = firstSlot.pass_groups.find((group) => {
                    return group.passes.some((pass) => pass.status === "ACTIVE");
                });
                if (groupWithActivePass) {
                    setSelectedCategory(groupWithActivePass.name);
                }
                else {
                    setSelectedCategory(firstSlot.pass_groups[0].name);
                }
            }
        }
    }, [slots, selectedSlotIdx]);
    // Preselect slot if directSlotParam or subEventParam is present
    useEffect(() => {
        if (directSlotParam && slots.length > 0) {
            const [, slotIdx] = directSlotParam.split("_");
            if (slotIdx && !isNaN(Number(slotIdx))) {
                setSelectedSlotIdx(Number(slotIdx));
                setHasSelectedSlot(true);
            }
        }
        else if (slots.length === 1 && selectedSlotIdx === null) {
            // Auto-select the only available slot
            setSelectedSlotIdx(slots[0].id);
            setHasSelectedSlot(true);
        }
        //  else if (subEventParam && slots.length > 0) {
        //   setSelectedSlotIdx(0); // Default to first slot of sub-event
        //   setHasSelectedSlot(true);
        // }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [directSlotParam, subEventParam, slots.length]);
    useEffect(() => {
        let isParamHasSlot = searchParams.get("slot") !== null;
        if (isParamHasSlot && slots.length > 0) {
            const slotParam = searchParams.get("slot");
            if (slotParam && !isNaN(Number(slotParam))) {
                const slotId = Number(slotParam);
                const slotExists = slots.some((s) => s.id === slotId);
                // Update if slot exists and is different from current selection
                if (slotExists && selectedSlotIdx !== slotId) {
                    setSelectedSlotIdx(slotId);
                    setHasSelectedSlot(true);
                }
            }
        }
    }, [selectedSlotIdx, slots, searchParams, selectedTab]);
    // Debounced modal open/close logic
    const debounceTimeoutRef = React.useRef(null);
    const debouncedModalControl = useCallback(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
            if (event &&
                !hasSelectedSlot &&
                (slots === null || slots === void 0 ? void 0 : slots.length) > 0 &&
                selectedTab === "Passes") {
                // If there's buffer time and table booking is disabled, always open modal for time slot selection
                if (event.buffer_time_minutes &&
                    event.buffer_time_minutes > 0 &&
                    !event.is_table_booking_enabled) {
                    onOpen();
                }
                // If there's only one slot, automatically select it instead of opening modal
                else if (slots.length === 1) {
                    setSelectedSlotIdx(slots[0].id);
                    setHasSelectedSlot(true);
                    onClose();
                }
                else {
                    onOpen();
                }
            }
            else {
                onClose();
            }
        }, 100);
    }, [
        event,
        hasSelectedSlot,
        slots,
        selectedTab,
        onOpen,
        onClose,
        setSelectedSlotIdx,
        setHasSelectedSlot,
    ]);
    // Auto open slot selection modal on initial load
    useEffect(() => {
        debouncedModalControl();
        // Cleanup timeout on unmount
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [debouncedModalControl]);
    // Show EventBookModal for walk-in bookings when no sub-event is selected
    useEffect(() => {
        if (bookingType === "walkin" &&
            !subEventParam &&
            (event === null || event === void 0 ? void 0 : event.has_sub_events) &&
            (event === null || event === void 0 ? void 0 : event.sub_events) &&
            !isLoading) {
            const now = new Date();
            // Find all currently active sub-events
            const activeSubEvents = event.sub_events.filter((subEvent) => {
                return subEvent.slots.some((slot) => {
                    const startTime = new Date(slot.start_time);
                    const endTime = new Date(slot.end_time);
                    return now >= startTime && now <= endTime;
                });
            });
            if (activeSubEvents.length === 1) {
                // Auto-select the single active sub-event
                const activeSubEvent = activeSubEvents[0];
                const queryParams = new URLSearchParams();
                queryParams.set("sub_event", `${activeSubEvent.name}_${activeSubEvent.id}`);
                navigate(`?${queryParams.toString()}`, { replace: true });
            }
            else if (activeSubEvents.length > 1) {
                // Multiple active sub-events - show modal for selection
                onEventBookModalOpen();
            }
            else {
                // No active sub-events - show modal to inform user
                onEventBookModalOpen();
            }
        }
        else if (bookingType === "walkin") {
            onEventBookModalClose();
        }
    }, [
        bookingType,
        event === null || event === void 0 ? void 0 : event.has_sub_events,
        event === null || event === void 0 ? void 0 : event.sub_events,
        isLoading,
        onEventBookModalOpen,
        onEventBookModalClose,
        navigate,
    ]);
    const slot = selectedSlotIdx !== null
        ? slots.find((s) => s.id === selectedSlotIdx)
        : undefined;
    // Use whats_happening_events from event data instead of separate API call
    const hasHappenings = (_e = (_d = (_c = data === null || data === void 0 ? void 0 : data.data) === null || _c === void 0 ? void 0 : _c.whats_happening_events) === null || _d === void 0 ? void 0 : _d.some((happening) => {
        var _a;
        return (_a = happening.placements) === null || _a === void 0 ? void 0 : _a.some((placement) => placement.levelType === "SLOT" && placement.levelRefId === (slot === null || slot === void 0 ? void 0 : slot.id));
    })) !== null && _e !== void 0 ? _e : false;
    const passCategories = useMemo(() => getPassCategories(slot), [slot]);
    const passes = useMemo(() => {
        if (!slot)
            return [];
        if (slot.has_pass_groups && slot.pass_groups) {
            if (selectedCategory === "All") {
                return slot.pass_groups.flatMap((g) => g.passes);
            }
            const group = slot.pass_groups.find((g) => g.name === selectedCategory);
            return group ? group.passes : [];
        }
        return slot.direct_passes || [];
    }, [slot, selectedCategory]);
    // Compute min/max price from passes
    const allPasses = useMemo(() => getAllPasses(slot), [slot]);
    // Initialize and update controlling pass quantities when passes or quantities change
    useEffect(() => {
        if (allPasses.length > 0) {
            const updatedControllingQuantities = {};
            // Find all controlling passes and their current quantities
            allPasses.forEach((pass) => {
                if (pass.is_restricted && pass.restricted_by_pass) {
                    const controllingPass = allPasses.find((p) => p.id === pass.restricted_by_pass);
                    if (controllingPass) {
                        updatedControllingQuantities[pass.restricted_by_pass] = {
                            quantity: quantities[pass.restricted_by_pass] || 0,
                            name: controllingPass.name,
                        };
                    }
                }
            });
            setControllingPassQuantities(updatedControllingQuantities);
        }
    }, [allPasses, quantities]);
    const minPrice = allPasses.length
        ? Math.min(...allPasses.map((p) => p.is_walkin_only
            ? p.walkin_prices.cover + p.walkin_prices.entry
            : p.booking_prices.cover + p.booking_prices.entry))
        : 0;
    const maxPrice = allPasses.length
        ? Math.max(...allPasses.map((p) => p.is_walkin_only
            ? p.walkin_prices.cover + p.walkin_prices.entry
            : p.booking_prices.cover + p.booking_prices.entry))
        : 10000;
    const [filterState, setFilterState] = useState({
        availability: [],
        passType: [],
        sortBy: "",
        priceRange: [0, 10000],
    });
    // Update price range when min/max prices are calculated
    useEffect(() => {
        setFilterState((prev) => ({
            ...prev,
            priceRange: [minPrice, maxPrice],
        }));
    }, [minPrice, maxPrice]);
    // Filter passes based on booking type
    const filteredPasses = useMemo(() => {
        let filtered = passes;
        // For walk-in bookings, show only walk-in passes or create default passes
        if (bookingType === "walkin") {
            const walkinPasses = filtered.filter((pass) => pass.is_walkin_only);
            if (walkinPasses.length > 0) {
                filtered = walkinPasses;
            }
            else {
                filtered = [];
            }
        }
        else {
            filtered = filtered.filter((pass) => !pass.is_walkin_only);
        }
        // Apply other filters
        // Availability
        if (filterState.availability.length) {
            filtered = filtered.filter((pass) => {
                return filterState.availability.includes(pass.status);
            });
        }
        // Pass Type
        if (filterState.passType.length) {
            filtered = filtered.filter((pass) => filterState.passType.includes(pass.pass_type));
        }
        // Price Range
        if (filterState.priceRange[0] !== minPrice ||
            filterState.priceRange[1] !== maxPrice) {
            filtered = filtered.filter((pass) => {
                const price = pass.is_walkin_only
                    ? pass.walkin_prices.cover + pass.walkin_prices.entry
                    : pass.booking_prices.cover + pass.booking_prices.entry;
                return (price >= filterState.priceRange[0] &&
                    price <= filterState.priceRange[1]);
            });
        }
        // Sort
        if (filterState.sortBy !== "") {
            filtered = [...filtered].sort((a, b) => {
                const priceA = a.is_walkin_only
                    ? a.walkin_prices.cover + a.walkin_prices.entry
                    : a.booking_prices.cover + a.booking_prices.entry;
                const priceB = b.is_walkin_only
                    ? b.walkin_prices.cover + b.walkin_prices.entry
                    : b.booking_prices.cover + b.booking_prices.entry;
                return filterState.sortBy === "low" ? priceA - priceB : priceB - priceA;
            });
        }
        return filtered;
    }, [passes, filterState, bookingType]);
    const autoSelectPasses = useCallback(() => {
        var _a;
        const maleCount = parseInt(searchParams.get("male-count") || "0", 10);
        const femaleCount = parseInt(searchParams.get("female-count") || "0", 10);
        // Only proceed if we have male or female counts
        if (maleCount === 0 && femaleCount === 0)
            return;
        // Only proceed if filteredPasses are available
        if (filteredPasses.length === 0)
            return;
        // Don't auto-select if there's a TICKET pass type available
        const hasTicketPass = filteredPasses.some((pass) => pass.pass_type === "TICKET");
        // Don't auto-select if slot has pass groups with "group" in the name
        const hasGroupPassGroup = (_a = slot === null || slot === void 0 ? void 0 : slot.has_pass_groups) !== null && _a !== void 0 ? _a : false;
        if (hasTicketPass || hasGroupPassGroup)
            return;
        // Check if we already have quantities selected (don't override user's manual selection)
        const hasExistingSelection = Object.values(quantities).some((qty) => qty > 0);
        if (hasExistingSelection)
            return;
        const newQuantities = {};
        // Find all pass types
        const couplePass = filteredPasses.find((pass) => pass.pass_type === "COUPLE" && pass.status === "ACTIVE");
        const stagPass = filteredPasses.find((pass) => pass.pass_type === "STAG" && pass.status === "ACTIVE");
        const ladiesPass = filteredPasses.find((pass) => pass.pass_type === "LADIES" && pass.status === "ACTIVE");
        const ticketPass = filteredPasses.find((pass) => pass.pass_type === "TICKET" && pass.status === "ACTIVE");
        // Logic for couple selection
        const coupleCount = Math.min(maleCount, femaleCount);
        let remainingMale = maleCount;
        let remainingFemale = femaleCount;
        // If both male and female counts are equal and couple pass exists
        if (coupleCount > 0 && couplePass) {
            if (!couplePass.is_restricted) {
                newQuantities[couplePass.id] = coupleCount;
            }
            else if (couplePass.restricted_by_pass) {
                // Couple is restricted, select its controlling pass first
                const requiredControlling = Math.ceil(coupleCount / (couplePass.restriction_ratio || 1));
                newQuantities[couplePass.restricted_by_pass] = Math.max(newQuantities[couplePass.restricted_by_pass] || 0, requiredControlling);
                // Then select the restricted couple pass itself
                newQuantities[couplePass.id] = coupleCount;
            }
            remainingMale -= coupleCount;
            remainingFemale -= coupleCount;
        }
        else if (coupleCount > 0 && !couplePass) {
            // If couple count exists but no couple pass, select stag and ladies separately
            if (stagPass && maleCount > 0) {
                if (!stagPass.is_restricted) {
                    newQuantities[stagPass.id] = maleCount;
                }
                else if (stagPass.restricted_by_pass) {
                    const requiredControlling = Math.ceil(maleCount / (stagPass.restriction_ratio || 1));
                    newQuantities[stagPass.restricted_by_pass] = Math.max(newQuantities[stagPass.restricted_by_pass] || 0, requiredControlling);
                    // Then select the restricted stag pass itself
                    newQuantities[stagPass.id] = maleCount;
                }
                remainingMale = 0;
            }
            if (ladiesPass && femaleCount > 0) {
                if (!ladiesPass.is_restricted) {
                    newQuantities[ladiesPass.id] = femaleCount;
                }
                else if (ladiesPass.restricted_by_pass) {
                    const requiredControlling = Math.ceil(femaleCount / (ladiesPass.restriction_ratio || 1));
                    newQuantities[ladiesPass.restricted_by_pass] = Math.max(newQuantities[ladiesPass.restricted_by_pass] || 0, requiredControlling);
                    // Then select the restricted ladies pass itself
                    newQuantities[ladiesPass.id] = femaleCount;
                }
                remainingFemale = 0;
            }
        }
        else {
            // For unequal counts, select individual passes
            if (stagPass && maleCount > 0) {
                if (!stagPass.is_restricted) {
                    newQuantities[stagPass.id] = maleCount;
                }
                else if (stagPass.restricted_by_pass) {
                    const requiredControlling = Math.ceil(maleCount / (stagPass.restriction_ratio || 1));
                    newQuantities[stagPass.restricted_by_pass] = Math.max(newQuantities[stagPass.restricted_by_pass] || 0, requiredControlling);
                    // Then select the restricted stag pass itself
                    newQuantities[stagPass.id] = maleCount;
                }
                remainingMale = 0;
            }
            if (ladiesPass && femaleCount > 0) {
                if (!ladiesPass.is_restricted) {
                    newQuantities[ladiesPass.id] = femaleCount;
                }
                else if (ladiesPass.restricted_by_pass) {
                    const requiredControlling = Math.ceil(femaleCount / (ladiesPass.restriction_ratio || 1));
                    newQuantities[ladiesPass.restricted_by_pass] = Math.max(newQuantities[ladiesPass.restricted_by_pass] || 0, requiredControlling);
                    // Then select the restricted ladies pass itself
                    newQuantities[ladiesPass.id] = femaleCount;
                }
                remainingFemale = 0;
            }
        }
        // Handle remaining males if any
        if (remainingMale > 0 && stagPass) {
            if (!stagPass.is_restricted) {
                newQuantities[stagPass.id] =
                    (newQuantities[stagPass.id] || 0) + remainingMale;
            }
            else if (stagPass.restricted_by_pass) {
                const requiredControlling = Math.ceil(remainingMale / (stagPass.restriction_ratio || 1));
                newQuantities[stagPass.restricted_by_pass] = Math.max(newQuantities[stagPass.restricted_by_pass] || 0, requiredControlling);
                // Then select the restricted stag pass itself
                newQuantities[stagPass.id] =
                    (newQuantities[stagPass.id] || 0) + remainingMale;
            }
        }
        // Handle remaining females if any
        if (remainingFemale > 0 && ladiesPass) {
            if (!ladiesPass.is_restricted) {
                newQuantities[ladiesPass.id] =
                    (newQuantities[ladiesPass.id] || 0) + remainingFemale;
            }
            else if (ladiesPass.restricted_by_pass) {
                const requiredControlling = Math.ceil(remainingFemale / (ladiesPass.restriction_ratio || 1));
                newQuantities[ladiesPass.restricted_by_pass] = Math.max(newQuantities[ladiesPass.restricted_by_pass] || 0, requiredControlling);
                // Then select the restricted ladies pass itself
                newQuantities[ladiesPass.id] =
                    (newQuantities[ladiesPass.id] || 0) + remainingFemale;
            }
        }
        // Calculate required controlling passes for restricted passes
        const controllingPassRequirements = {};
        filteredPasses.forEach((pass) => {
            if (pass.is_restricted &&
                pass.restricted_by_pass &&
                pass.restriction_ratio) {
                const selectedQty = newQuantities[pass.id] || 0;
                if (selectedQty > 0) {
                    // Calculate minimum required controlling pass quantity
                    const requiredControlling = Math.ceil(selectedQty / pass.restriction_ratio);
                    controllingPassRequirements[pass.restricted_by_pass] = Math.max(controllingPassRequirements[pass.restricted_by_pass] || 0, requiredControlling);
                }
            }
        });
        // Auto-select controlling passes based on requirements
        Object.entries(controllingPassRequirements).forEach(([passId, quantity]) => {
            newQuantities[parseInt(passId)] = quantity;
        });
        // Handle ticket pass - select if no other passes were selected or if it's available
        if (ticketPass && Object.keys(newQuantities).length === 0) {
            // If no male/female counts provided but ticket pass exists, select 1 ticket
            newQuantities[ticketPass.id] = 0;
        }
        // Only set quantities if we have something to set
        if (Object.keys(newQuantities).length > 0) {
            setQuantities(newQuantities);
        }
    }, [filteredPasses, searchParams, quantities, slot]);
    // Trigger auto-selection only on initial load when URL params are present
    useEffect(() => {
        // Only auto-select if we have URL params and no existing selections
        const maleCount = parseInt(searchParams.get("male-count") || "0", 10);
        const femaleCount = parseInt(searchParams.get("female-count") || "0", 10);
        const hasUrlParams = maleCount > 0 || femaleCount > 0;
        const hasExistingSelection = Object.values(quantities).some((qty) => qty > 0);
        if (hasUrlParams && !hasExistingSelection && filteredPasses.length > 0) {
            autoSelectPasses();
        }
    }, [filteredPasses, slot]);
    useEffect(() => {
        if (passes.length && bookingType === "walkin") {
            const walkinPass = passes.find((pass) => pass.is_walkin_only);
            if (!walkinPass) {
                navigate(`/event/booking/confirm/${event_id}/${selectedSlotIdx}/0-0-0/walkin`, { replace: true });
            }
        }
    }, [passes, bookingType, event_id]);
    // Count active filters for badge
    const isPriceRangeFiltered = filterState.priceRange[0] !== minPrice ||
        filterState.priceRange[1] !== maxPrice;
    const activeFilterCount = filterState.availability.length +
        filterState.passType.length +
        (filterState.sortBy !== "" ? 1 : 0) +
        (isPriceRangeFiltered ? 1 : 0);
    // Calculate total
    const total = filteredPasses.reduce((sum, pass) => {
        const qty = quantities[pass.id] || 0;
        return (sum +
            qty *
                (pass.is_walkin_only
                    ? pass.walkin_prices.cover_discount +
                        pass.walkin_prices.entry_discount >
                        0
                        ? pass.walkin_prices.cover +
                            pass.walkin_prices.entry -
                            (pass.walkin_prices.cover_discount +
                                pass.walkin_prices.entry_discount)
                        : pass.walkin_prices.cover + pass.walkin_prices.entry
                    : pass.booking_prices.cover_discount +
                        pass.booking_prices.entry_discount >
                        0
                        ? pass.booking_prices.cover +
                            pass.booking_prices.entry -
                            (pass.booking_prices.cover_discount +
                                pass.booking_prices.entry_discount)
                        : pass.booking_prices.cover + pass.booking_prices.entry));
    }, 0);
    // Handlers
    const handleQtyChange = (passId, delta) => {
        const newQuantities = { ...quantities };
        const currentQty = newQuantities[passId] || 0;
        const newQty = Math.max(0, currentQty + delta);
        // Don't proceed if quantity would be negative
        if (newQty < 0)
            return;
        // Update controlling pass quantities tracking
        const updatedControllingQuantities = { ...controllingPassQuantities };
        // Check if this pass is a controlling pass for any restricted pass
        const isControllingPass = allPasses.some((pass) => pass.is_restricted && pass.restricted_by_pass === passId);
        if (isControllingPass) {
            const pass = allPasses.find((p) => p.id === passId);
            if (pass) {
                updatedControllingQuantities[passId] = {
                    quantity: newQty,
                    name: pass.name,
                };
            }
        }
        // Set the new quantity for the changed pass
        newQuantities[passId] = newQty;
        allPasses.forEach((pass) => {
            if (pass.is_restricted && pass.restricted_by_pass === passId) {
                const controllingPass = allPasses.find((p) => p.id === passId);
                if (controllingPass) {
                    updatedControllingQuantities[passId] = {
                        quantity: newQty,
                        name: controllingPass.name,
                    };
                }
                // Calculate the maximum allowed quantity based on the ratio
                const maxAllowed = Math.floor(newQty * (pass.restriction_ratio || 1));
                const currentRestrictedQty = newQuantities[pass.id] || 0;
                // If current restricted quantity exceeds the new maximum, reduce it to max
                if (currentRestrictedQty > maxAllowed) {
                    newQuantities[pass.id] = maxAllowed;
                }
            }
        });
        // Update both states together
        setControllingPassQuantities(updatedControllingQuantities);
        setQuantities(newQuantities);
    };
    const handleCategoryChange = (cat) => {
        setSelectedCategory(cat);
        setQuantities({});
    };
    const handleConfirm = () => {
        var _a;
        if (Object.keys(quantities).length === 0) {
            customToast({
                title: "Select at least one pass",
                status: "warning",
            });
            return;
        }
        if (event && ((_a = event.pub_id) === null || _a === void 0 ? void 0 : _a.toString()) === "338") {
            const totalPasses = Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
            const hasCouplePass = filteredPasses
                .filter((pass) => quantities[pass.id] > 0)
                .some((pass) => pass.pass_type === "COUPLE");
            if (totalPasses < 2 && !hasCouplePass) {
                customToast({
                    title: "Minimum 2 passes required",
                    description: "Please select at least 2 passes to continue",
                    status: "warning",
                });
                return;
            }
        }
        // Save selections to localStorage
        if (event_id) {
            localStorage.setItem(`event_selections_${event_id}`, JSON.stringify({
                slotIdx: selectedSlotIdx,
                quantities,
                category: selectedCategory,
            }));
            localStorage.removeItem(`z_selected_table_${event_id}`);
        }
        // Create query string with additional context
        const queryParams = new URLSearchParams();
        // Add sub-event information if applicable
        if ((event === null || event === void 0 ? void 0 : event.has_sub_events) && subEventParam) {
            queryParams.set("sub_event", subEventParam);
        }
        // Add direct slot information if applicable
        if (directSlotParam) {
            queryParams.set("direct_slot", directSlotParam);
        }
        // Add selected passes information
        const selectedPasses = filteredPasses
            .filter((pass) => quantities[pass.id] > 0)
            .map((pass) => {
            var _a, _b;
            return ({
                passId: pass.id,
                quantity: quantities[pass.id],
                passGroup: (slot === null || slot === void 0 ? void 0 : slot.has_pass_groups)
                    ? (_b = (_a = slot.pass_groups) === null || _a === void 0 ? void 0 : _a.find((g) => g.passes.some((p) => p.id === pass.id))) === null || _b === void 0 ? void 0 : _b.name
                    : undefined,
            });
        });
        queryParams.set("passes", JSON.stringify(selectedPasses));
        // Check if time is already selected from URL params (from pax modal), otherwise use slot start time
        const selectedParamTime = searchParams.get("time");
        if (selectedParamTime) {
            // Use the selected time from URL params
            queryParams.set("time", selectedParamTime);
        }
        else {
            // Fallback to slot start time if no time selected
            const currentSlot = slot ||
                (selectedSlotIdx !== null
                    ? slots.find((s) => s.id === selectedSlotIdx)
                    : null);
            if (currentSlot === null || currentSlot === void 0 ? void 0 : currentSlot.start_time) {
                try {
                    const slotStartTime = new Date(currentSlot.start_time);
                    if (!isNaN(slotStartTime.getTime())) {
                        const formattedTime = format(slotStartTime, "yyyy-MM-dd'T'HH:mm:ss");
                        queryParams.set("time", formattedTime);
                    }
                }
                catch (e) {
                    console.error("Error formatting slot start time:", e);
                }
            }
        }
        if (bookingType === "walkin") {
            navigate(`/event/booking/confirm/${event_id}/${selectedSlotIdx}/${bookingType}?${queryParams.toString()}`);
        }
        else {
            navigate(`/event/booking/confirm/${event_id}/${selectedSlotIdx}?${queryParams.toString()}`);
        }
    };
    if (isLoading)
        return (_jsx(Flex, { h: "100vh", align: "center", justify: "center", children: _jsx(Spinner, { color: "white" }) }));
    if (error || !event)
        return (_jsx(Flex, { h: "100vh", align: "center", justify: "center", children: _jsx(Text, { children: "Error loading event." }) }));
    return (_jsxs(Box, { minH: "100vh", bg: "#111111", color: "white", pb: 24, fontSize: "xs", children: [_jsx(BackNavigation, { heading: selectedTab === "Tables" ? "" : "Select Passes", subHeading: selectedTab === "Tables" ? "" : event.event_name }), _jsxs(Box, { w: "100%", maxW: ["100%", "100%", "100%", "400px"], mx: "auto", bg: "#111111", position: "relative", pt: event.is_pass_booking_enabled && event.is_table_booking_enabled
                    ? 0
                    : 2, children: [bookingType !== "walkin" &&
                        event.is_table_booking_enabled &&
                        event.is_pass_booking_enabled && (_jsx(Flex, { w: "100%", justifyContent: "center", mb: 2, bg: "#111111", position: "sticky", top: 16, pt: 4, pb: 2, zIndex: 10, children: _jsx(TabLayout, { tabs: [
                                {
                                    name: "Passes",
                                    label: "Passes",
                                    icon: _jsx(PassIcon, { color: "#898F8F" }),
                                    iconSelected: _jsx(PassIcon, { color: "white" }),
                                },
                                {
                                    name: "Tables",
                                    label: "Tables",
                                    iconSelected: _jsx(BookTableIcon, { color: "white" }),
                                    icon: _jsx(BookTableIcon, { color: "#898F8F" }),
                                },
                            ], selectedColor: "white", selectedBg: "brand", solidSlider: true, width: "fit-content", size: "sm", selectedTab: selectedTab, onTabChange: (tab) => {
                                setSelectedTab(tab);
                                // Update URL parameter to reflect the selected tab
                                const newSearchParams = new URLSearchParams(location.search);
                                newSearchParams.set("step", searchParams.get("time") &&
                                    searchParams.get("male-count") &&
                                    searchParams.get("female-count")
                                    ? "2"
                                    : "1");
                                newSearchParams.set("tab", tab);
                                navigate(`?${newSearchParams.toString()}`, {
                                    replace: true,
                                });
                            } }) })), selectedTab === "Tables" && _jsx(EventTableBooking, { event: event }), selectedTab === "Passes" && (_jsxs(Box, { px: 4, py: 16, children: [!((event === null || event === void 0 ? void 0 : event.buffer_time_minutes) &&
                                event.buffer_time_minutes > 0 &&
                                !event.is_table_booking_enabled) && (_jsxs(Flex, { align: "center", position: "sticky", top: bookingType !== "walkin" && event.is_table_booking_enabled
                                    ? 32
                                    : 16, pt: 4, pb: 4, zIndex: 10, bg: "#111111", children: [_jsx(Text, { fontWeight: 500, fontSize: "xs", mr: 2, children: "Time Slot:" }), _jsx(Text, { fontWeight: 700, fontSize: "xs", children: slot
                                            ? `${format(new Date(slot.start_time), "h:mm a")} - ${format(new Date(slot.end_time), "h:mm a")}`
                                            : "-" }), _jsx(Button, { size: "xs", variant: "link", color: "#A383FF", fontSize: "xs", fontWeight: "bold", pl: 2, pr: 0, ml: "auto", minW: 0, h: "auto", rightIcon: _jsx(ChevronRightIcon, { boxSize: 5 }), _hover: { textDecoration: "underline" }, onClick: onOpen, children: "Change" })] })), _jsx(Divider, { mb: 4, borderColor: "#232323" }), slot && hasHappenings && (_jsx(Flex, { align: "center", justify: "flex-start", mb: 4, children: _jsx(Text, { fontSize: "xs", color: "#7F54FB", cursor: "pointer", fontWeight: "500", _hover: { color: "#9F74FB" }, onClick: () => {
                                        // Navigate to slot whats happening page
                                        navigate(`/slot/${slot.id}/whats-happening`, {
                                            state: { eventId: event_id },
                                        });
                                    }, children: "EXPLORE WHAT'S HAPPENING \u2192" }) })), (event === null || event === void 0 ? void 0 : event.has_sub_events) && !hasSelectedSlot ? (_jsxs(Flex, { direction: "column", align: "center", justify: "center", p: 8, textAlign: "center", bg: "#1E1E1E", borderRadius: 12, mb: 6, children: [_jsx(Text, { fontSize: "sm", fontWeight: 500, mb: 2, children: "Please select a time slot first" }), _jsx(Text, { fontSize: "xs", color: "gray.400", mb: 4, children: "Choose a time slot to view available passes" }), _jsx(Button, { colorScheme: "purple", bg: "brand", onClick: onOpen, size: "sm", fontWeight: 400, borderRadius: 8, children: "Select Time Slot" })] })) : (_jsxs(_Fragment, { children: [passCategories.length > 1 && (_jsxs(HStack, { w: "100%", mb: 4, spacing: 2, overflowX: "auto", children: [passCategories.length > 1 && (_jsx(Button, { size: "sm", fontWeight: 500, fontSize: "xs", px: 4, py: 2, h: "52px", w: "fit-content", minW: "55px", flexShrink: 0, bg: selectedCategory === "All" ? "white" : "#1E1E1E", border: "1px solid", borderColor: selectedCategory === "All" ? "white" : "#3E3E3E", color: selectedCategory === "All" ? "#111" : "white", borderRadius: 12, boxShadow: selectedCategory === "All" ? "md" : "none", onClick: () => handleCategoryChange("All"), children: "All" })), passCategories
                                                .filter((cat) => cat !== "All")
                                                .map((cat) => {
                                                // Check if all passes in this category are sold out
                                                const categoryGroup = (slot === null || slot === void 0 ? void 0 : slot.has_pass_groups) && (slot === null || slot === void 0 ? void 0 : slot.pass_groups)
                                                    ? slot.pass_groups.find((g) => g.name === cat)
                                                    : null;
                                                const isAllSoldOut = categoryGroup
                                                    ? (() => {
                                                        // Filter passes based on booking type
                                                        const relevantPasses = bookingType === "walkin"
                                                            ? categoryGroup.passes.filter((pass) => pass.is_walkin_only)
                                                            : categoryGroup.passes.filter((pass) => !pass.is_walkin_only);
                                                        // Check if all passes are sold out
                                                        return (relevantPasses.length > 0 &&
                                                            relevantPasses.every((pass) => pass.status === "SOLD_OUT"));
                                                    })()
                                                    : false;
                                                return (_jsx(Button, { size: "sm", fontWeight: 500, fontSize: "xs", px: 4, py: 2, h: "52px", w: "fit-content", minW: "55px", flexShrink: 0, bg: selectedCategory === cat ? "white" : "#1E1E1E", color: selectedCategory === cat ? "#111" : "white", border: "1px solid", borderColor: selectedCategory === cat ? "white" : "#3E3E3E", borderRadius: 12, boxShadow: selectedCategory === cat ? "md" : "none", onClick: () => handleCategoryChange(cat), children: _jsxs(Flex, { flexDir: "column", align: "center", children: [cat, _jsx(Text, { fontSize: "10px", color: "subtle", fontWeight: "normal", mt: 0.5, display: "block", children: isAllSoldOut
                                                                    ? "SOLD OUT"
                                                                    : slot &&
                                                                        slot.has_pass_groups &&
                                                                        slot.pass_groups
                                                                        ? `${getPriceRange(slot.pass_groups.find((g) => g.name === cat) || slot).priceText}`
                                                                        : slot
                                                                            ? `${getPriceRange(slot).priceText}`
                                                                            : "-" })] }) }, cat));
                                            })] })), slot && selectedCategory === "All" && (_jsx(Flex, { justify: "flex-end", mb: 4, children: _jsxs(Button, { leftIcon: _jsx(FaFilter, { size: 16 }), size: "md", variant: "outline", colorScheme: "purple", color: "white", bg: "#1A1A1A", borderColor: "#3E3E3E", borderRadius: 12, fontSize: "xs", fontWeight: "400", onClick: () => updateFilterDrawerUrl(true), position: "relative", children: ["Filters", activeFilterCount > 0 && (_jsx(Badge, { ml: 2, colorScheme: "purple", borderRadius: "full", children: activeFilterCount }))] }) })), _jsx(Box, { mb: 4, children: _jsx(FastTrackEntryInfoCard, { bookingCloseTime: event.booking_close_time }) }), slot && (_jsxs(Box, { children: [passCategories.length > 1 &&
                                                selectedCategory !== "All" && (_jsxs(_Fragment, { children: [_jsxs(Flex, { alignItems: "center", width: "100%", mb: 2, children: [_jsx(Text, { fontSize: "2xl", color: "white", fontFamily: "Zuumerough-Regular", children: selectedCategory.toUpperCase() }), _jsx(Box, { flex: "1", height: "1px", bg: "#3A3D43" })] }), _jsx(Text, { fontSize: "xs", color: "#bbb", whiteSpace: "pre-wrap", mb: 8, children: (() => {
                                                            if (selectedCategory !== "All" &&
                                                                slot.has_pass_groups &&
                                                                slot.pass_groups) {
                                                                const group = slot.pass_groups.find((g) => g.name === selectedCategory);
                                                                if (group && group.description) {
                                                                    return group.description;
                                                                }
                                                            }
                                                            return "";
                                                        })() })] })), _jsx(VStack, { spacing: 4, align: "stretch", children: filteredPasses
                                                    .sort((a, b) => a.id - b.id)
                                                    .map((pass, idx) => {
                                                    var _a, _b;
                                                    let groupName = undefined;
                                                    let groupBg = undefined;
                                                    if (selectedCategory === "All" &&
                                                        slot.has_pass_groups &&
                                                        slot.pass_groups) {
                                                        const groupIdx = slot.pass_groups.findIndex((g) => g.passes.some((p) => p.id === pass.id));
                                                        if (groupIdx !== -1) {
                                                            groupName = slot.pass_groups[groupIdx].name;
                                                            groupBg = getPassGroupTagBg(groupIdx);
                                                        }
                                                    }
                                                    return (_jsx(PassCard, { pass: pass, quantity: quantities[pass.id] || 0, onQuantityChange: handleQtyChange, groupName: groupName, groupBg: groupBg, controllingPassQuantity: pass.is_restricted && pass.restricted_by_pass
                                                            ? ((_a = controllingPassQuantities[pass.restricted_by_pass]) === null || _a === void 0 ? void 0 : _a.quantity) || 0
                                                            : 0, controllingPassName: pass.is_restricted && pass.restricted_by_pass
                                                            ? (_b = controllingPassQuantities[pass.restricted_by_pass]) === null || _b === void 0 ? void 0 : _b.name
                                                            : undefined, isWalkinPass: bookingType === "walkin" }, pass.id));
                                                }) })] })), slot &&
                                        quantities &&
                                        Object.values(quantities).some((qty) => qty > 0) && (_jsxs(Flex, { position: "fixed", bottom: 0, left: 0, w: "100vw", mx: "auto", bg: "#181818", p: 4, justify: "space-between", align: "center", zIndex: 10, style: { boxShadow: "0 -2px 16px #0008" }, children: [_jsxs(Box, { children: [_jsx(Text, { fontWeight: 400, fontSize: "xs", color: "subtle", children: "Total" }), _jsx(Text, { fontWeight: 700, fontSize: "xl", letterSpacing: 0.8, style: { fontFamily: "monospace" }, children: total
                                                            ? `₹${total >= 10000 ? total.toLocaleString() : total}`
                                                            : "Free" })] }), _jsx(Button, { colorScheme: "purple", bg: "brand", borderRadius: "5", px: 8, fontSize: "xs", fontWeight: "bold", onClick: handleConfirm, isDisabled: bookingClosed, opacity: bookingClosed ? 0.5 : 1, children: "Confirm Booking" })] }))] }))] }))] }), isEventBookModalOpen && (_jsx(EventBookModal, { isOpen: isEventBookModalOpen, onOpen: onEventBookModalOpen, onClose: onEventBookModalClose, event: event, showTableBooking: false })), isOpen && (_jsx(ResponsiveModal, { isOpen: isOpen, onOpen: onOpen, onClose: onClose, paramKey: "time-slot-modal", header: ((event === null || event === void 0 ? void 0 : event.buffer_time_minutes) && event.buffer_time_minutes > 0) ||
                    ((event === null || event === void 0 ? void 0 : event.is_pass_booking_enabled) && (event === null || event === void 0 ? void 0 : event.is_table_booking_enabled))
                    ? "Select Time"
                    : "Select Time Slot", showCloseButton: true, size: "md", children: ((event === null || event === void 0 ? void 0 : event.buffer_time_minutes) && event.buffer_time_minutes > 0) ||
                    ((event === null || event === void 0 ? void 0 : event.is_pass_booking_enabled) && (event === null || event === void 0 ? void 0 : event.is_table_booking_enabled)) ? (_jsx(SelectPaxAndTime, { event: event, selectedSubEventId: subEventParam
                        ? parseInt(subEventParam.split("_")[1])
                        : undefined, initialSelectedTime: searchParams.get("time"), initialSelectedSlotId: selectedSlotIdx, hidePaxSelection: true, onConfirm: ({ selectedDate, selectedTime, slotId }) => {
                        const formattedTime = `${selectedDate}T${selectedTime}`;
                        const queryParams = new URLSearchParams(location.search);
                        queryParams.set("time", formattedTime);
                        queryParams.set("slot", slotId.toString());
                        const selectedSlot = slots.find((s) => s.id === slotId);
                        if (selectedSlot) {
                            queryParams.set("direct_slot", `${selectedSlot.name}_${selectedSlot.id}`);
                        }
                        setSelectedSlotIdx(slotId);
                        setHasSelectedSlot(true);
                        setQuantities({});
                        navigate(-1);
                        onClose();
                        setTimeout(() => {
                            navigate(`?${queryParams.toString()}`, { replace: true });
                        }, 10);
                    }, onCancel: () => navigate(-1) })) : (_jsx(SlotPillsGrid, { eventId: event_id || "", slots: slots, selectedIdx: selectedSlotIdx !== null && selectedSlotIdx !== void 0 ? selectedSlotIdx : undefined, onSlotChange: (slot) => {
                        const index = slots.findIndex((s) => s.name === slot.name &&
                            s.start_time === slot.start_time &&
                            s.end_time === slot.end_time);
                        if (index !== -1) {
                            setSelectedSlotIdx(slot.id);
                            setHasSelectedSlot(true);
                            setQuantities({});
                            const queryParams = new URLSearchParams(location.search);
                            const slotStartTime = new Date(slot.start_time);
                            const formattedTime = format(slotStartTime, "yyyy-MM-dd'T'HH:mm:ss");
                            queryParams.set("time", formattedTime);
                            queryParams.set("slot", slot.id.toString());
                            queryParams.set("direct_slot", `${slot.name}_${slot.id}`);
                            navigate(-1);
                            onClose();
                            setTimeout(() => {
                                navigate(`?${queryParams.toString()}`, { replace: true });
                            }, 10);
                        }
                    } })) })), _jsx(PassesFilterDrawer, { isOpen: isFilterOpen, onClose: () => updateFilterDrawerUrl(false), passes: allPasses, initialFilters: filterState, onApply: (filters) => {
                    setFilterState(filters);
                    updateFilterDrawerUrl(false);
                }, onClearAll: () => {
                    setFilterState({
                        availability: [],
                        passType: [],
                        sortBy: "",
                        priceRange: [minPrice, maxPrice],
                    });
                    updateFilterDrawerUrl(false);
                } })] }));
};
export default BookEvent;
